import { useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import { Avatar } from '../components/ui/Avatar'
import { Icon } from '../components/ui/Icon'
import { MenuItem, MenuLabel, MenuSeparator, Popover } from '../components/ui/Popover'
import { useToast } from '../components/ui/Toast'

/** Account, organisation, language and the way out. */
export const UserMenu = (): ReactElement | null => {
  const t = useT()
  const { user, orgs, activeOrg, switchOrg, logout, isSuperadmin } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  if (!user) return null

  const go = (path: string): void => {
    setOpen(false)
    void navigate(path)
  }

  const signOut = async (): Promise<void> => {
    setOpen(false)
    await logout()
    toast({ title: t('shell.userMenu.signedOut'), tone: 'info' })
    void navigate('/login')
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-tour="usermenu-trigger"
        onClick={() => {
          setOpen((previous) => !previous)
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('common.shell.userMenu', { name: user.name })}
        className="ring-focus rounded-full transition-opacity hover:opacity-85"
      >
        <Avatar name={user.name} src={user.avatarUrl} size="sm" surface="chrome" />
      </button>

      <Popover
        open={open}
        onClose={() => {
          setOpen(false)
        }}
        anchorRef={triggerRef}
        placement="bottom"
        align="end"
        surface="chrome"
        role="menu"
        label={t('common.shell.account')}
        className="w-64 py-1"
      >
        <div className="px-3 py-2">
          <p className="t-small truncate font-semibold text-chrome-ink">{user.name}</p>
          <p className="t-mono truncate text-chrome-mute">{user.email}</p>
        </div>

        {orgs.length > 1 ? (
          <>
            <MenuSeparator surface="chrome" />
            <MenuLabel surface="chrome">{t('common.shell.organisation')}</MenuLabel>
            {orgs.map((org) => (
              <MenuItem
                key={org.id}
                surface="chrome"
                active={org.id === activeOrg?.id}
                icon={
                  org.id === activeOrg?.id ? (
                    <Icon name="check" size={15} />
                  ) : (
                    <span className="inline-block w-3.75" />
                  )
                }
                onSelect={() => {
                  switchOrg(org.id)
                  setOpen(false)
                }}
              >
                {org.name}
              </MenuItem>
            ))}
          </>
        ) : null}

        <MenuSeparator surface="chrome" />
        <MenuItem
          surface="chrome"
          icon={<Icon name="user" size={15} />}
          data-tour="usermenu-settings"
          onSelect={() => {
            go('/app/settings')
          }}
        >
          {t('common.shell.accountSettings')}
        </MenuItem>
        <MenuItem
          surface="chrome"
          icon={<Icon name="brands" size={15} />}
          data-tour="usermenu-brands"
          onSelect={() => {
            go('/app/brands')
          }}
        >
          {t('common.nav.brands')}
        </MenuItem>
        <MenuItem
          surface="chrome"
          icon={<Icon name="billing" size={15} />}
          onSelect={() => {
            go('/app/billing')
          }}
        >
          {t('common.shell.planAndBilling')}
        </MenuItem>
        {isSuperadmin ? (
          <MenuItem
            surface="chrome"
            icon={<Icon name="admin" size={15} />}
            onSelect={() => {
              go('/app/admin')
            }}
          >
            {t('common.shell.adminConsole')}
          </MenuItem>
        ) : null}
        <MenuItem
          surface="chrome"
          icon={<Icon name="palette" size={15} />}
          onSelect={() => {
            go('/system')
          }}
        >
          {t('shell.userMenu.designSystem')}
        </MenuItem>

        <MenuSeparator surface="chrome" />
        <MenuItem
          surface="chrome"
          icon={<Icon name="logout" size={15} />}
          onSelect={() => {
            void signOut()
          }}
        >
          {t('common.actions.signOut')}
        </MenuItem>
      </Popover>
    </>
  )
}
